import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Modal } from "./UI";
import { UserRound, ShieldCheck, LogOut, Settings2, Megaphone, LifeBuoy, Rocket, Mail, Phone, Clock3, BookOpen, LayoutList, LayoutPanelLeft } from "lucide-react";

const ROLE_LABELS = { admin: "Administrator", clinician: "Clinician", front_desk: "Front Desk", billing: "Billing" };

const DENSITY_KEY = "cocoa.density";
export function applyDensity(mode) {
  const m = mode === "compact" ? "compact" : "comfortable";
  document.documentElement.setAttribute("data-density", m);
  try { localStorage.setItem(DENSITY_KEY, m); } catch { /* ignore */ }
}
export function loadDensity() {
  try { return localStorage.getItem(DENSITY_KEY) || "comfortable"; } catch { return "comfortable"; }
}

let metaCache = null;
const fetchMeta = () => (metaCache = metaCache || api.get("/app-meta").then((r) => r.data));

export function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [density, setDensity] = useState(loadDensity());
  const ref = useRef(null);

  useEffect(() => { fetchMeta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { applyDensity(density); }, [density]);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const item = (testId, Icon, label, onClick, danger) => (
    <button data-testid={testId} onClick={() => { setOpen(false); onClick(); }}
      className={`flex w-full items-center gap-2.5 px-4 py-2 text-sm hover:bg-[#F5F2EB] ${danger ? "text-[#C62828]" : ""}`}>
      <Icon size={15} className={danger ? "" : "text-[#7B6157]"} /> {label}
    </button>
  );
  const released = (meta?.updates || []).filter((u) => u.type === "released");
  const upcoming = (meta?.updates || []).filter((u) => u.type === "upcoming");

  return (
    <div className="relative ml-2" ref={ref}>
      <button data-testid="profile-menu-btn" onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-full bg-white/10 pl-1 pr-3 py-1 hover:bg-white/20 transition-colors">
        <div className="h-7 w-7 rounded-full bg-[#8D6E63] flex items-center justify-center text-xs font-bold">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-xs font-semibold leading-tight">{user?.first_name} {user?.last_name}</div>
          <div className="text-[10px] text-white/60 leading-tight">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
      </button>
      {open && (
        <div data-testid="profile-menu-dropdown" className="absolute right-0 mt-2 w-64 rounded-2xl bg-white text-[#3E2723] shadow-[0_8px_32px_-6px_rgba(78,52,46,0.2)] border border-[#EFEBE0] py-2 z-[95]">
          <div className="px-4 pb-2 pt-1 border-b border-[#F1EEE7]">
            <div className="text-sm font-bold">{user?.first_name} {user?.last_name}</div>
            <div className="text-xs text-[#7B6157]">{user?.email}</div>
            {user?.tenant?.name && <div className="mt-0.5 text-[10px] text-[#84675B]">{user.tenant.name}</div>}
          </div>
          <div className="py-1">
            {item("profile-my-profile-btn", UserRound, "My Profile", () => navigate(`/staff/${user?.id}`))}
            {item("profile-preferences-btn", Settings2, "Preferences", () => navigate("/preferences"))}
            {item("profile-security-btn", ShieldCheck, "Security & 2FA", () => navigate("/security"))}
          </div>
          <div className="border-t border-[#F1EEE7] px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] px-1 pb-1.5">Header density</div>
            <div className="flex items-center gap-1.5" data-testid="density-toggle-group">
              <button data-testid="density-comfortable-btn" onClick={() => setDensity("comfortable")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${density === "comfortable" ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
                <LayoutPanelLeft size={13} /> Comfortable
              </button>
              <button data-testid="density-compact-btn" onClick={() => setDensity("compact")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${density === "compact" ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
                <LayoutList size={13} /> Compact
              </button>
            </div>
          </div>
          <div className="border-t border-[#F1EEE7] py-1">
            {item("profile-updates-btn", Megaphone, "Product Updates", () => setUpdatesOpen(true))}
            {item("profile-support-btn", LifeBuoy, "Contact & Support", () => setSupportOpen(true))}
          </div>
          <div className="border-t border-[#F1EEE7] py-1">
            {item("logout-btn", LogOut, "Sign out", logout, true)}
          </div>
          <div className="px-4 pt-1.5 text-[10px] text-[#84675B]" data-testid="profile-menu-version">
            Cocoa EHR {meta?.version ? `v${meta.version}` : ""}{meta?.released_at ? ` · released ${meta.released_at}` : ""}
          </div>
        </div>
      )}

      <Modal open={updatesOpen} onClose={() => setUpdatesOpen(false)} title="Product Updates" wide>
        <div className="space-y-5" data-testid="product-updates-modal">
          {upcoming.map((u) => (
            <div key={u.title} className="rounded-2xl border border-[#FFE0B2] bg-[#FFF8E1] p-4">
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-[#B84A00]"><Rocket size={15} /> {u.title}{u.version && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#B84A00]">v{u.version}</span>}</div>
              <ul className="mt-2 space-y-1 text-sm text-[#5D4037]">{u.notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#B84A00]">•</span>{n}</li>)}</ul>
            </div>
          ))}
          {released.map((u) => (
            <div key={u.version} className="rounded-2xl border border-[#F1EEE7] p-4">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-[#4E342E] px-2.5 py-0.5 text-[11px] font-bold text-white">v{u.version}</span>
                <span className="font-heading text-sm font-bold text-[#3E2723]">{u.title}</span>
                <span className="ml-auto text-xs text-[#84675B]">{u.date}</span>
              </div>
              <ul className="mt-2 space-y-1 text-sm text-[#5D4037]">{u.notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#7B6157]">•</span>{n}</li>)}</ul>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={supportOpen} onClose={() => setSupportOpen(false)} title="Contact & Support">
        <div className="space-y-3 text-sm" data-testid="support-modal">
          {meta?.support?.email && <a href={`mailto:${meta.support.email}`} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3 hover:bg-[#F5F2EB]" data-testid="support-email"><Mail size={16} className="text-[#7B6157]" /><span><b className="block text-[#3E2723]">Email support</b><span className="text-[#7B6157]">{meta.support.email}</span></span></a>}
          {meta?.support?.phone && <div className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3"><Phone size={16} className="text-[#7B6157]" /><span><b className="block text-[#3E2723]">Phone</b><span className="text-[#7B6157]">{meta.support.phone}</span></span></div>}
          {meta?.support?.hours && <div className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3"><Clock3 size={16} className="text-[#7B6157]" /><span><b className="block text-[#3E2723]">Support hours</b><span className="text-[#7B6157]">{meta.support.hours}</span></span></div>}
          {meta?.support?.docs_url && <a href={meta.support.docs_url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3 hover:bg-[#F5F2EB]" data-testid="support-docs"><BookOpen size={16} className="text-[#7B6157]" /><span><b className="block text-[#3E2723]">Help center & documentation</b><span className="text-[#7B6157]">{meta.support.docs_url}</span></span></a>}
          <p className="text-[11px] text-[#84675B]">For your privacy, never include client health information in support emails.</p>
        </div>
      </Modal>
    </div>
  );
}
